import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldCheck } from "lucide-react";
import { setIntent } from "../lib/nav";
import { SPOTLIGHTS } from "../data/content";

const MotionLink = motion(Link);

const Spotlight = () => {
    return (
        <section id="partners" className="relative py-24 lg:py-32" data-testid="partners">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                    <div>
                        <div className="pill"><span className="accent-dot" />Open lanes for partners</div>
                        <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">
                            Sell IndoBlitz<br />
                            <span className="italic text-gold-shimmer">in your market.</span>
                        </h2>
                    </div>
                    <p className="max-w-md text-ivory/60 lg:text-right">
                        We are actively opening commercial lanes with independent BD partners, consultants and agencies.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                    {SPOTLIGHTS.map((s, i) => {
                        const isExternal = s.ctaHref.startsWith("http");
                        const inner = (
                            <>
                                <div className="flex items-start justify-between">
                                    <span className="text-4xl leading-none">{s.flag}</span>
                                    <ArrowUpRight className="w-5 h-5 text-ivory/30 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                                </div>
                                <div className="mt-6 font-mono text-xs uppercase tracking-widest text-gold/80">{s.region}</div>
                                <h3 className="mt-2 font-display text-2xl text-ivory leading-tight">{s.title}</h3>
                                <p className="mt-4 text-sm text-ivory/65 leading-relaxed flex-1">{s.text}</p>
                                <div className="mt-6 pt-5 border-t border-white/5 text-gold text-sm font-medium flex items-center gap-2">
                                    {s.cta}
                                    <ArrowUpRight className="w-4 h-4" />
                                </div>
                            </>
                        );
                        const anim = {
                            initial: { opacity: 0, y: 24 },
                            whileInView: { opacity: 1, y: 0 },
                            viewport: { once: true, margin: "-80px" },
                            transition: { duration: 0.6, delay: i * 0.08 },
                            className: "glass glass-hover rounded-2xl p-8 flex flex-col group",
                            "data-testid": `spotlight-${i}`,
                        };
                        return isExternal ? (
                            <motion.a key={s.region} href={s.ctaHref} target="_blank" rel="noopener noreferrer" {...anim}>
                                {inner}
                            </motion.a>
                        ) : (
                            <MotionLink key={s.region} to="/contact" onClick={() => setIntent(s.intent)} {...anim}>
                                {inner}
                            </MotionLink>
                        );
                    })}
                </div>

                {/* Impact Partnership - for CSR + Government + Multilateral funders */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 relative rounded-2xl overflow-hidden"
                    style={{
                        background:
                            "linear-gradient(120deg, rgba(212,168,87,0.10) 0%, rgba(45,212,191,0.08) 50%, rgba(20,20,26,0.7) 100%)",
                        border: "1px solid rgba(212,168,87,0.28)",
                        boxShadow: "0 30px 80px -40px rgba(212,168,87,0.3)",
                    }}
                    data-testid="impact-partnership-banner"
                >
                    <div
                        className="absolute -right-24 -top-24 w-80 h-80 opacity-25 pointer-events-none blur-2xl"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(212,168,87,0.6), transparent 60%)",
                        }}
                    />

                    <div className="relative grid lg:grid-cols-12 gap-8 p-8 lg:p-12 items-center">
                        <div className="lg:col-span-8">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/10 text-gold text-[10px] font-mono uppercase tracking-widest">
                                <ShieldCheck className="w-3 h-3" />
                                Impact partnership · CSR · Government · Multilateral
                            </div>
                            <h3 className="mt-5 font-display text-4xl lg:text-5xl text-ivory tracking-tight leading-[1.05]">
                                Build the movement <span className="italic text-gold-shimmer">with us.</span>
                            </h3>
                            <p className="mt-4 text-ivory/75 leading-relaxed max-w-2xl">
                                IndoBlitz is a <span className="text-gold">30-year cross-border legacy project</span> - training rural India's young talent for global delivery, mapped to <span className="text-gold">Schedule&nbsp;VII (ii) employability</span> and <span className="text-gold">(x) rural development</span> categories, and aligned to Skill India, Digital India and BharatNet missions.
                            </p>
                            <p className="mt-3 text-ivory/70 leading-relaxed max-w-2xl">
                                We invite Central &amp; State Government agencies, Section&nbsp;135 CSR partners, family foundations, multilateral organisations and impact investors to <span className="text-gold">co-build measurable rural outcomes</span> at scale - with reporting, governance and audit cadence built in.
                            </p>

                            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
                                {[
                                    "Schedule VII (ii) · Employability",
                                    "Schedule VII (x) · Rural development",
                                    "Skill India / NSDC alignment",
                                    "Digital India · BharatNet",
                                    "Audit-ready impact reporting",
                                    "Founder-reviewed, no middlemen",
                                ].map((tag) => (
                                    <li key={tag} className="flex items-start gap-2 text-sm text-ivory/75">
                                        <Sparkles className="w-3.5 h-3.5 text-gold mt-1 shrink-0" />
                                        <span>{tag}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-4">
                            <div className="text-left lg:text-right">
                                <div className="font-display text-5xl lg:text-6xl text-gold-shimmer leading-none">30-yr</div>
                                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ivory/50">legacy project · rural India → global</div>
                            </div>
                            <Link
                                to="/contact"
                                onClick={() => setIntent("impact-funder")}
                                className="btn-gold !py-3 !px-5"
                                data-testid="impact-cta"
                            >
                                Open a partnership conversation
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <p className="text-[11px] text-ivory/45 max-w-xs lg:text-right">
                                Founder reviews every funder enquiry within 48 hours. Decisions, not committees.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Spotlight;
